import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  SpinnerComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import {
  CardPartnerCard,
  CardPartnerData,
  CardPartnerDepositAddress,
  CardPartnerListKind,
  CardPartnerTransaction,
  CardPartnerTransfer,
  MyCardsService
} from '../../services/mycards.service';
import {
  cardPartnerAccountColor,
  cardPartnerCardColor,
  cardPartnerChainLabel,
  cardPartnerCopy,
  cardPartnerCrypto,
  cardPartnerKeyOr,
  cardPartnerKycColor,
  cardPartnerMoney,
  cardPartnerNetworkLabel,
  cardPartnerShortHash,
  cardPartnerStatusColor,
  cardPartnerTxAmount,
  cardPartnerTxDirection
} from '../../utils/card-partner-format';

interface AddressGroup {
  network: string;
  label: string;
  address: string;
  memo: string | null;
  tokens: string[];
}

/**
 * What the card partner (cryptocards.cash) holds about the member: account + KYC
 * state, cards, deposit addresses, card transactions, crypto deposits and
 * withdrawals. Everything comes from OUR database (mirrored by the backend's
 * ToonpaySyncService); the Refresh button asks the backend to pull this member's
 * data from the partner now. Values stay in the partner's vocabulary until here.
 */
@Component({
  selector: 'app-card-partner-data',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    BadgeComponent,
    SpinnerComponent,
    TableDirective,
    TranslatePipe
  ],
  templateUrl: './card-partner-data.component.html',
  styleUrls: ['./card-partner-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardPartnerDataComponent implements OnInit {
  private service = inject(MyCardsService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  static readonly PAGE_SIZE = 20;

  /** Only a member with a card entitlement gets the "not registered yet" note. */
  entitled = input<boolean>(false);

  data = signal<CardPartnerData | null>(null);
  loading = signal(true);
  refreshing = signal(false);
  refreshHint = signal<string | null>(null);
  copied = signal<string | null>(null);

  transactions = signal<CardPartnerTransaction[]>([]);
  deposits = signal<CardPartnerTransfer[]>([]);
  withdrawals = signal<CardPartnerTransfer[]>([]);
  listBusy = signal<CardPartnerListKind | null>(null);

  linked = computed(() => this.data()?.linked === true);
  account = computed(() => this.data()?.account ?? null);
  cards = computed<CardPartnerCard[]>(() => this.data()?.cards ?? []);
  addressGroups = computed<AddressGroup[]>(() => this.groupAddresses(this.data()?.deposit_addresses ?? []));
  transactionsTotal = computed(() => this.data()?.transactions_total ?? 0);
  depositsTotal = computed(() => this.data()?.deposits_total ?? 0);
  withdrawalsTotal = computed(() => this.data()?.withdrawals_total ?? 0);

  ngOnInit(): void {
    this.service.cardPartner()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => { this.apply(d); this.loading.set(false); },
        error: () => this.loading.set(false)
      });
  }

  refresh(): void {
    if (this.refreshing()) return;
    this.refreshing.set(true);
    this.refreshHint.set(null);
    this.service.refreshCardPartner()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => {
          this.refreshing.set(false);
          this.apply(d);
          if (d.refresh_error) this.refreshHint.set('mycards.partner.refresh_error');
        },
        error: (e) => {
          this.refreshing.set(false);
          this.refreshHint.set(e?.status === 429 ? 'mycards.partner.refresh_throttled' : 'mycards.partner.refresh_error');
        }
      });
  }

  loadMore(kind: CardPartnerListKind): void {
    const current = kind === 'transactions' ? this.transactions() : kind === 'deposits' ? this.deposits() : this.withdrawals();
    const size = CardPartnerDataComponent.PAGE_SIZE;
    // The page load delivered the newest 50; continue by page index over what we already hold.
    const page = Math.floor(current.length / size) + 1;
    this.listBusy.set(kind);
    this.service.cardPartnerList<any>(kind, page, size)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.listBusy.set(null);
          const seen = new Set(current.map(r => r.id));
          const merged = [...current, ...res.items.filter((r: { id: string }) => !seen.has(r.id))];
          if (kind === 'transactions') this.transactions.set(merged);
          else if (kind === 'deposits') this.deposits.set(merged);
          else this.withdrawals.set(merged);
        },
        error: () => this.listBusy.set(null)
      });
  }

  private apply(d: CardPartnerData): void {
    this.data.set(d);
    this.transactions.set(d.transactions ?? []);
    this.deposits.set(d.deposits ?? []);
    this.withdrawals.set(d.withdrawals ?? []);
  }

  private groupAddresses(rows: CardPartnerDepositAddress[]): AddressGroup[] {
    // The backend already orders networks (BNB Smart Chain first); keep that order.
    const groups: AddressGroup[] = [];
    for (const r of rows) {
      let g = groups.find(x => x.network === r.network && x.address === r.address);
      if (!g) {
        g = { network: r.network, label: this.networkLabel(r.network), address: r.address, memo: r.memo, tokens: [] };
        groups.push(g);
      }
      if (!g.tokens.includes(r.token)) g.tokens.push(r.token);
    }
    return groups;
  }

  // ── labels ────────────────────────────────────────────────────────────────

  /** i18n key for a known partner value, else the raw value (the pipe passes unknown strings through). */
  keyOr(kind: string, value: string | null | undefined): string {
    return cardPartnerKeyOr(kind, value);
  }

  networkLabel(network: string): string {
    return cardPartnerNetworkLabel(network);
  }

  chainLabel(chain: string | null): string {
    return cardPartnerChainLabel(chain);
  }

  kycColor(status: string | null | undefined): string {
    return cardPartnerKycColor(status);
  }

  accountColor(status: string | null | undefined): string {
    return cardPartnerAccountColor(status);
  }

  cardColor(status: string | null | undefined): string {
    return cardPartnerCardColor(status);
  }

  statusColor(status: string | null | undefined): string {
    return cardPartnerStatusColor(status);
  }

  /** Member still has something to do at the partner. */
  kycActionNeeded(status: string | null | undefined): boolean {
    return ['NOT_STARTED', 'IN_PROGRESS', 'RESUBMIT', 'PENDING_CARDHOLDER', 'CONTACT_SUPPORT'].includes(status ?? '');
  }

  // ── amounts ───────────────────────────────────────────────────────────────

  /**
   * Amounts arrive positive for both directions, so the sign comes from the type:
   * a load onto the card is money in, a purchase or a fee is money out. A fee row
   * carries its value in `fee` with amount 0.
   */
  txDirection(t: CardPartnerTransaction): 'in' | 'out' | 'none' {
    return cardPartnerTxDirection(t);
  }

  txAmount(t: CardPartnerTransaction): string {
    return cardPartnerTxAmount(t, this.locale());
  }

  /** Merchant-side amount when it differs from the billing currency (e.g. 16.10 EUR billed as 18.45 USD). */
  txOriginal(t: CardPartnerTransaction): string | null {
    const amount = Number(t.transaction_amount ?? 0);
    if (!amount || !t.transaction_currency || t.transaction_currency === t.currency) return null;
    return `${this.money(amount)} ${t.transaction_currency}`;
  }

  txFee(t: CardPartnerTransaction): string | null {
    if ((t.type ?? '').startsWith('Fee_')) return null;
    const fee = Number(t.fee ?? 0);
    return fee > 0 ? `${this.money(fee)} ${t.currency ?? ''}`.trim() : null;
  }

  txDescription(t: CardPartnerTransaction): string {
    if (t.merchant_name) {
      const place = [t.merchant_city, t.merchant_country].filter(Boolean).join(', ');
      return place ? `${t.merchant_name} · ${place}` : t.merchant_name;
    }
    return '';
  }

  money(value: number): string {
    return cardPartnerMoney(value, this.locale());
  }

  /** Crypto amounts: keep up to 8 decimals, drop trailing zeros. */
  crypto(value: string | null): string {
    return cardPartnerCrypto(value, this.locale());
  }

  shortHash(hash: string | null): string {
    return cardPartnerShortHash(hash);
  }

  // ── dates ─────────────────────────────────────────────────────────────────

  date(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(this.locale());
  }

  dateTime(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleString(this.locale(), { dateStyle: 'short', timeStyle: 'short' });
  }

  relative(d: string | null | undefined): string {
    if (!d) return '—';
    const seconds = Math.round((new Date(d).getTime() - Date.now()) / 1000);
    const abs = Math.abs(seconds);
    const rtf = new Intl.RelativeTimeFormat(this.locale(), { numeric: 'auto' });
    if (abs < 60) return rtf.format(seconds, 'second');
    if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
    return rtf.format(Math.round(seconds / 86400), 'day');
  }

  private locale(): string {
    try {
      return this.translate.getActiveLang() || 'en';
    } catch {
      return 'en';
    }
  }

  // ── clipboard ─────────────────────────────────────────────────────────────

  copy(value: string | null): void {
    if (!value) return;
    cardPartnerCopy(value).then(ok => {
      if (!ok) return;
      this.copied.set(value);
      setTimeout(() => { if (this.copied() === value) this.copied.set(null); }, 2000);
    });
  }
}
